import React, { Fragment } from 'react';
import { WIDGET_TYPES, API_URL } from './constants';
import { classifyAsyncDataResponse } from './utils/async-data-retry';
import { LocaleProvider, useI18n } from './i18n/index.jsx';
import { ERROR_EVENT, PAYLOAD_EVENT, buildAsyncDataUrl, toFontStack } from './utils/embed-host.js';
import { applyLocaleDirection } from './utils/text-direction.js';

// Main embed component
export const SessionboardEmbedWidget = ({
  embedId,
  dataUrl,
  injectPayload,
  host,
  widgetType,
  language,
  WidgetComponent = Fragment,
  shadowRoot,
  container,
}) => {
  const [embedData, setEmbedData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // SB-9250: the host page pushes payloads (builder preview); no network.
  React.useEffect(() => {
    if (!injectPayload || !host) return undefined;

    const apply = (payload) => {
      if (payload === undefined || payload === null) return;
      setError(null);
      setEmbedData(payload);
      setLoading(false);
    };
    const onPayload = (event) => apply(event.detail);

    host.addEventListener(PAYLOAD_EVENT, onPayload);
    apply(host.payload);
    return () => host.removeEventListener(PAYLOAD_EVENT, onPayload);
  }, [injectPayload, host]);

  React.useEffect(() => {
    if (injectPayload) return undefined;

    if (!embedId && !dataUrl) {
      setError('embedId is required');
      setLoading(false);
      return;
    }

    if (!widgetType || !Object.values(WIDGET_TYPES).includes(widgetType)) {
      setError('widgetType is required and must be one of: ' + Object.values(WIDGET_TYPES).join(', '));
      setLoading(false);
      return;
    }

    if (!WidgetComponent) {
      setError('WidgetComponent is required');
      setLoading(false);
      return;
    }

    let cancelled = false;
    let retryTimerId = null;

    const fetchData = async (attempt = 0) => {
      let failure = { status: null, code: null };
      try {
        // Get client timezone, fallback to UTC if not supported
        const clientTimezone =
          typeof Intl === 'object' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';

        // SB-8809: the widget's language attribute rides along; the API
        // normalizes it (attribute > embed default > English) and echoes the
        // resolved locale back in the payload for chrome localization.
        const response = await fetch(
          buildAsyncDataUrl({
            apiUrl: API_URL,
            embedId,
            dataUrl,
            widgetType,
            clientTimezone,
            language,
            ran: new Date().getTime(),
          }),
          {
            headers: {
              'Cache-Control': 'max-age=604800',
            },
          }
        );

        let body;
        try {
          body = await response.json();
        } catch (parseErr) {
          if (response.ok) throw parseErr;
          body = {};
        }

        if (cancelled) return;

        const outcome = classifyAsyncDataResponse({
          status: response.status,
          ok: response.ok,
          body,
          attempt,
          retryAfter: response.headers.get('Retry-After'),
        });

        if (outcome.action === 'retry') {
          // API is still building the data cache (202 {status:'building'}) —
          // stay in the loading state and poll again shortly. (SB-8666)
          retryTimerId = setTimeout(() => fetchData(attempt + 1), outcome.delayMs);
          return;
        }

        if (outcome.action === 'error') {
          failure = { status: response.status, code: (body && body.code) || null };
          throw new Error(outcome.message);
        }

        /** @type {import('./types/async-data.js').AsyncDataResponse} */
        const result = outcome.data;
        setEmbedData(result);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (host) {
          host.dispatchEvent(new CustomEvent(ERROR_EVENT, { detail: { ...failure, message: err.message } }));
        }
        setError(err.message);
        setLoading(false);
      }
    };
    fetchData();

    return () => {
      cancelled = true;
      if (retryTimerId) {
        clearTimeout(retryTimerId);
      }
    };
  }, [embedId, dataUrl, injectPayload, widgetType, language, host]);

  // SB-9253: Site payloads carry the Site's font; widget CSS pins its own
  // font-family on many selectors, so override inside the shadow root.
  const fontStack = toFontStack(embedData?.fontFamily);
  React.useEffect(() => {
    if (!fontStack || !host) return undefined;
    if (!host.shadowRoot) {
      const previous = host.style.fontFamily;
      host.style.fontFamily = fontStack;
      return () => {
        host.style.fontFamily = previous;
      };
    }
    const style = document.createElement('style');
    style.setAttribute('data-sb-font-family', '');
    style.textContent = `:host, * { font-family: ${fontStack} !important; }`;
    host.shadowRoot.appendChild(style);
    return () => style.remove();
  }, [fontStack, host]);

  React.useEffect(() => {
    if (embedData?.extraCss) {
      const style = document.createElement('style');
      style.id = 'embed-custom-styles';
      style.textContent = embedData.extraCss;
      const stylesElement = shadowRoot
        ? shadowRoot.getElementById('sessionboard-embed-styles')
        : document.getElementById('sessionboard-embed-styles');
      if (stylesElement) {
        // Insert the new style element right after the existing one
        stylesElement.insertAdjacentElement('afterend', style);
      } else {
        document.head.appendChild(style);
      }

      return () => {
        style.remove();
      };
    }
  }, [embedData, shadowRoot]);

  // SB-8809: chrome localizes from the API-resolved locale once the payload
  // arrives; before that (loading/error) the requested attribute is the best
  // available signal so even the error strings match the page language.
  const effectiveLocale = embedData?.language || language || null;

  // SB-9141: layout effect so an RTL widget never paints a mirrored-wrong frame.
  React.useLayoutEffect(() => {
    applyLocaleDirection(container, effectiveLocale);
  }, [container, effectiveLocale]);

  return (
    <LocaleProvider locale={effectiveLocale}>
      <EmbedBody embedData={embedData} loading={loading} error={error} WidgetComponent={WidgetComponent} />
    </LocaleProvider>
  );
};

const EmbedBody = ({ embedData, loading, error, WidgetComponent }) => {
  const { t } = useI18n();

  if (error) {
    return (
      <div className="embed-error">
        {t('error_loading')} {error}
      </div>
    );
  }
  if (embedData?.isDisabled) {
    return <div className="embed-disabled">{t('widget_disabled')}</div>;
  }

  return <WidgetComponent data={embedData} loading={loading} />;
};
